/**
 * Admin → Users: per-user telemetry, newest signup first. A quick read on
 * who's signing up and how they're actually using the product — coach
 * responses (total + last 7d), conversations, signals, last active, and
 * daily streak. Paginated; counts are bounded server-side aggregations.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getUsersTelemetry,
  type UserTelemetryItem,
} from "@/features/admin/lib/adminApi";
import { limitCell, type LimitTone } from "@/features/admin/lib/limits";
import { relativeTime } from "@/shared/format";
import { UserDetailPanel } from "./UserDetailPanel";

// Bumped from 25 so the whole user base loads on one page at our scale —
// client-side column sorting (below) then sorts effectively the ENTIRE set,
// not just a slice. Past ~100 users this becomes per-page sorting and we'd
// move sorting server-side (a `sort`/`dir` param on GET /admin/users).
const PAGE_SIZE = 100;

const TONE_CLASS: Record<LimitTone, string> = {
  muted: "text-text-muted",
  ok: "text-text",
  near: "text-amber-500",
  over: "text-danger",
};

// Column sort keys + how each maps to a comparable value. Numeric/date
// columns sort desc-first (most interesting at the top); the text column
// sorts asc-first.
type SortKey =
  | "user"
  | "joined"
  | "tier"
  | "limit"
  | "responses"
  | "responses7d"
  | "convos"
  | "signals"
  | "lastActive"
  | "streak";

interface SortState {
  key: SortKey;
  dir: "asc" | "desc";
}

const TEXT_KEYS: ReadonlySet<SortKey> = new Set<SortKey>(["user"]);

// Paid > free, then plan tier — so "Tier" sorts by how valuable the account is.
function tierRank(u: UserTelemetryItem): number {
  const statusRank =
    u.subscriptionStatus === "active"
      ? 3
      : u.subscriptionStatus === "past_due"
        ? 2
        : u.subscriptionStatus === "canceled"
          ? 1
          : 0; // free / null
  const planRank =
    u.planLevel === "unlimited"
      ? 3
      : u.planLevel === "plus"
        ? 2
        : u.planLevel === "core"
          ? 1
          : 0;
  return statusRank * 10 + planRank;
}

function sortValue(u: UserTelemetryItem, key: SortKey): number | string {
  switch (key) {
    case "user":
      return (u.email ?? u.userId).toLowerCase();
    case "joined":
      return u.createdAt ? Date.parse(u.createdAt) : 0;
    case "tier":
      return tierRank(u);
    case "limit":
      return u.responsesThisMonth ?? 0;
    case "responses":
      return u.coachResponsesTotal;
    case "responses7d":
      return u.coachResponses7d;
    case "convos":
      return u.conversationsTotal;
    case "signals":
      return u.signalCount;
    case "lastActive":
      return u.lastActiveAt ? Date.parse(u.lastActiveAt) : 0;
    case "streak":
      return u.dailyStreak;
  }
}

function sortUsers(
  items: UserTelemetryItem[],
  { key, dir }: SortState,
): UserTelemetryItem[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (typeof av === "string" && typeof bv === "string") {
      return av.localeCompare(bv) * sign;
    }
    return ((av as number) - (bv as number)) * sign;
  });
}

export function UsersView() {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>({ key: "joined", dir: "desc" });
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["admin-users", page],
    queryFn: () => getUsersTelemetry({ page, limit: PAGE_SIZE }),
  });

  if (isLoading) return <p className="text-sm text-text-muted">Loading users…</p>;
  if (isError || !data)
    return <p className="text-sm text-danger">Couldn't load users.</p>;

  // Clicking a header sorts by it; clicking the active header flips direction.
  // A fresh column starts desc for numbers/dates, asc for text.
  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: TEXT_KEYS.has(key) ? "asc" : "desc" },
    );

  const rows = sortUsers(data.items, sort);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-text-muted border-b border-border">
              <SortTh label="User" sortKey="user" sort={sort} onSort={onSort} />
              <SortTh label="Joined" sortKey="joined" sort={sort} onSort={onSort} />
              <SortTh label="Tier" sortKey="tier" sort={sort} onSort={onSort} />
              <SortTh label="Limit" sortKey="limit" sort={sort} onSort={onSort} />
              <SortTh label="Resp." sortKey="responses" sort={sort} onSort={onSort} align="right" />
              <SortTh label="7d" sortKey="responses7d" sort={sort} onSort={onSort} align="right" />
              <SortTh label="Convos" sortKey="convos" sort={sort} onSort={onSort} align="right" />
              <SortTh label="Signals" sortKey="signals" sort={sort} onSort={onSort} align="right" />
              <SortTh label="Last active" sortKey="lastActive" sort={sort} onSort={onSort} />
              <SortTh label="🔥" sortKey="streak" sort={sort} onSort={onSort} align="right" />
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <UserRow key={u.userId} u={u} onClick={() => setSelected(u.userId)} />
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <UserDetailPanel userId={selected} onClose={() => setSelected(null)} />
      )}

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          disabled={page === 0 || isFetching}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="rounded-lg border border-border text-text-muted px-4 py-2 disabled:opacity-40 hover:bg-text/5 hover:text-text transition"
        >
          ← Prev
        </button>
        <span className="text-text-muted/80 font-mono tabular-nums text-xs">
          page {page + 1} · {data.pageMeta.total} users
        </span>
        <button
          type="button"
          disabled={!data.pageMeta.hasNext || isFetching}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-lg border border-border text-text-muted px-4 py-2 disabled:opacity-40 hover:bg-text/5 hover:text-text transition"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === sortKey;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
  return (
    <th className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={[
          "inline-flex items-center gap-1 uppercase tracking-wider transition hover:text-text",
          align === "right" ? "flex-row-reverse" : "",
          active ? "text-text" : "",
        ].join(" ")}
        aria-label={`Sort by ${label}`}
      >
        <span>{label}</span>
        <span className="text-[9px] w-2 text-accent">{arrow}</span>
      </button>
    </th>
  );
}

function UserRow({ u, onClick }: { u: UserTelemetryItem; onClick: () => void }) {
  const tier =
    u.subscriptionStatus === "free" || !u.subscriptionStatus
      ? "Free"
      : `${u.subscriptionStatus}${u.planLevel ? ` · ${u.planLevel}` : ""}`;
  const limit = limitCell(u);
  // A user who has never produced a coach response is a fresh signup that
  // bounced before drilling — worth eyeballing. Dim those rows.
  const cold = u.coachResponsesTotal === 0;
  return (
    <tr
      onClick={onClick}
      className={[
        "border-b border-border/40 last:border-0 cursor-pointer hover:bg-text/5 transition",
        cold ? "text-text-muted/60" : "text-text",
      ].join(" ")}
    >
      <td className="px-3 py-2 max-w-[180px] truncate" title={u.userId}>
        {u.email ?? <span className="font-mono text-xs">{u.userId.slice(0, 8)}…</span>}
      </td>
      <td className="px-3 py-2 text-text-muted whitespace-nowrap">
        {u.createdAt ? relativeTime(u.createdAt) : "—"}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">{tier}</td>
      <td
        className={`px-3 py-2 whitespace-nowrap font-mono tabular-nums ${TONE_CLASS[limit.tone]}`}
      >
        {limit.label}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums">
        {u.coachResponsesTotal}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-text-muted">
        {u.coachResponses7d}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums">
        {u.conversationsTotal}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums text-text-muted">
        {u.signalCount}
      </td>
      <td className="px-3 py-2 text-text-muted whitespace-nowrap">
        {u.lastActiveAt ? relativeTime(u.lastActiveAt) : "never"}
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums">
        {u.dailyStreak > 0 ? u.dailyStreak : "—"}
      </td>
    </tr>
  );
}
